import { navigatorClient } from "@/api";
import type { ApiResponse } from "@/types";

// F05 Contact Us (backend /contact/*)
export type InquirySource = "gnb" | "agent_detail" | "case_study" | "ai_assistant";

export interface FormContext {
  inquiry_types: { value: string; label: string }[];
  default_type: string;
  prefill: {
    name: string | null;
    position: string | null;
    email: string | null;
    phone: string | null;
    company: string | null;
    industry: string | null;
    company_size: string | null;
  };
  attachment: {
    agent_id?: string | null;
    agent_name?: string | null;
    supplier_name?: string | null;
    case_study_id?: string | null;
    case_title?: string | null;
    assistant_session_id?: string | null;
    source_label: string;
  } | null;
  consents: string[];
  // 제3자 제공 동의가 있어야 접수되는 유형 (도입 · 컨설팅)과 회원의 현재 동의 여부
  third_party_types: string[];
  third_party_agreed: boolean;
}

export interface InquiryReceipt {
  id: string;
  inquiry_type: string;
  inquiry_type_label: string;
  team_name: string;
  team_email: string;
}

export interface MemberInquiryPayload {
  inquiry_type: string;
  source: InquirySource;
  agent_id?: string;
  case_study_id?: string;
  assistant_session_id?: string;
  name: string;
  // 회원 유형마다 화면 항목이 달라요: 수요자는 모두, 공급자 · PwC 내부는 position(직함 · 직급)만
  position?: string;
  email: string;
  phone: string;
  company?: string;
  industry?: string;
  company_size?: string;
  content: string;
  consents: string[];
  idempotency_key: string;
}

export interface GuestInquiryPayload {
  name: string;
  email: string;
  phone: string;
  company: string;
  content: string;
  consents: string[];
  idempotency_key: string;
}

export interface ContactTarget {
  source: InquirySource;
  agent_id?: string;
  case_study_id?: string;
  assistant_session_id?: string;
}

export async function getFormContext(target: ContactTarget, signal?: AbortSignal): Promise<FormContext> {
  const { data } = await navigatorClient.get<ApiResponse<FormContext>>("/contact/form-context", { params: target, signal });
  return data.data!;
}

export async function submitMemberInquiry(payload: MemberInquiryPayload): Promise<InquiryReceipt> {
  const { data } = await navigatorClient.post<ApiResponse<InquiryReceipt>>("/contact/inquiries", payload);
  return data.data!;
}

// 개인정보 제3자 제공 동의 팝업의 [동의하기] (회원 단위로 남아요)
export async function agreeThirdParty(): Promise<void> {
  await navigatorClient.post<ApiResponse<{ agreed: boolean }>>("/contact/third-party-consent");
}

export async function submitGuestInquiry(payload: GuestInquiryPayload): Promise<InquiryReceipt> {
  const { data } = await navigatorClient.post<ApiResponse<InquiryReceipt>>("/contact/inquiries/guest", payload);
  return data.data!;
}

// Builds the Contact Us link other screens use (에이전트 상세 · Case Study · AI Assistant).
export function contactPath(target: ContactTarget): string {
  const params = new URLSearchParams();
  Object.entries(target).forEach(([key, value]) => value && params.set(key, value));
  return `/contact?${params.toString()}`;
}
